import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import api from '../api/axiosConfig';
import './CadastroAplicadorPage.css';
import Footer from './Footer';

const CadastroAplicadorPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    telefone: '',
    cpfCnpj: '',
    instagram: '',
    studioNome: '',
    studioCep: '',
    studioEndereco: '',
    studioNumero: '',
    studioComplemento: '',
    studioBairro: '',
    studioCidade: '',
    studioEstado: ''
  });
  const [docType, setDocType] = useState('cpf');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const formatPhone = (value) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const formatCPF = (value) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  const formatCNPJ = (value) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
  };

  const handleDocChange = (e) => {
    const formatted = docType === 'cpf' ? formatCPF(e.target.value) : formatCNPJ(e.target.value);
    setFormData({ ...formData, cpfCnpj: formatted });
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, telefone: formatted });
  };

  const formatCEP = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
  };

  const handleCepChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setFormData(prev => ({ ...prev, studioCep: formatted }));
    setCepError('');
  };

  const buscarCep = async () => {
    const digits = formData.studioCep.replace(/\D/g, '');
    console.log('[CEP] buscarCep chamado, digits:', digits);
    if (digits.length !== 8) return;
    setCepLoading(true);
    setCepError('');
    try {
      const url = `https://viacep.com.br/ws/${digits}/json/`;
      console.log('[CEP] Fetching:', url);
      const resp = await fetch(url);
      console.log('[CEP] Response status:', resp.status);
      const data = await resp.json();
      console.log('[CEP] Data:', data);
      if (data.erro) {
        setCepError(t('register.studioCepError'));
      } else {
        setFormData(prev => ({
          ...prev,
          studioEndereco: data.logradouro || '',
          studioBairro: data.bairro || '',
          studioCidade: data.localidade || '',
          studioEstado: data.uf || ''
        }));
      }
    } catch (err) {
      console.error('[CEP] Erro:', err);
      setCepError(t('register.studioCepError'));
    } finally {
      setCepLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setSuccess(false);

    if (formData.senha !== formData.confirmarSenha) {
      setMessage(t('register.passwordMismatch'));
      return;
    }

    if (formData.senha.length < 8) {
      setMessage(t('register.passwordMinLength'));
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.senha)) {
      setMessage(t('register.passwordComplexity'));
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/api/cadastro-aplicador', {
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        telefone: formData.telefone,
        cpfCnpj: formData.cpfCnpj,
        tipoDocumento: docType,
        instagram: formData.instagram
      });

      if (response.data.success) {
        // Save studio after successful applicator registration
        if (formData.studioNome && formData.studioCep && formData.studioEndereco && formData.studioNumero) {
          try {
            // Login to get token for studio creation
            const loginResp = await api.post('/api/login', {
              email: formData.email,
              senha: formData.senha
            });
            if (loginResp.data.success && loginResp.data.token) {
              await api.post('/api/estudios', {
                nome: formData.studioNome,
                cep: formData.studioCep.replace(/\D/g, ''),
                endereco: formData.studioEndereco,
                numero: formData.studioNumero,
                complemento: formData.studioComplemento,
                bairro: formData.studioBairro,
                cidade: formData.studioCidade,
                estado: formData.studioEstado
              }, { headers: { Authorization: `Bearer ${loginResp.data.token}` } });
            }
          } catch (studioErr) {
            console.error('Erro ao salvar estúdio:', studioErr);
          }
        }
        setSuccess(true);
        setMessage(t('register.successMessage'));
        setFormData({
          nome: '',
          email: '',
          senha: '',
          confirmarSenha: '',
          telefone: '',
          cpfCnpj: '',
          instagram: '',
          studioNome: '',
          studioCep: '',
          studioEndereco: '',
          studioNumero: '',
          studioComplemento: '',
          studioBairro: '',
          studioCidade: '',
          studioEstado: ''
        });
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        setMessage(error.response.data.message);
      } else {
        setMessage(t('register.errorMessage'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cadastro-aplicador-container">
      <div className="cadastro-aplicador-header">
        <button onClick={() => navigate('/login')} className="back-btn">{t('common.backToLogin')}</button>
        <Link to="/" className="logo-link">
          <div className="logo-wrapper-small">
            <div className="logo-circle-small">
              <img src="/logo.png" alt="IGL Coatings Logo" />
            </div>
          </div>
        </Link>
        <h1>{t('register.title')}</h1>
        <p>{t('register.subtitle')}</p>
      </div>

      <div className="cadastro-aplicador-content">
        <form onSubmit={handleSubmit} className="aplicador-form">
          
          <div className="form-group">
            <label>{t('register.fullName')} *</label>
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              placeholder={t('register.fullNamePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('register.email')} *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('register.emailPlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('register.cpfCnpj')} *</label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
              <button
                type="button"
                onClick={() => { setDocType('cpf'); setFormData({ ...formData, cpfCnpj: '' }); }}
                style={{
                  flex: 1, padding: '8px', borderRadius: '8px', border: '2px solid',
                  borderColor: docType === 'cpf' ? '#00B140' : '#e0e0e0',
                  background: docType === 'cpf' ? '#00B140' : 'transparent',
                  color: docType === 'cpf' ? 'white' : '#666',
                  fontWeight: 600, cursor: 'pointer', fontSize: '14px'
                }}
              >CPF</button>
              <button
                type="button"
                onClick={() => { setDocType('cnpj'); setFormData({ ...formData, cpfCnpj: '' }); }}
                style={{
                  flex: 1, padding: '8px', borderRadius: '8px', border: '2px solid',
                  borderColor: docType === 'cnpj' ? '#00B140' : '#e0e0e0',
                  background: docType === 'cnpj' ? '#00B140' : 'transparent',
                  color: docType === 'cnpj' ? 'white' : '#666',
                  fontWeight: 600, cursor: 'pointer', fontSize: '14px'
                }}
              >CNPJ</button>
            </div>
            <input
              type="text"
              name="cpfCnpj"
              value={formData.cpfCnpj}
              onChange={handleDocChange}
              placeholder={docType === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('register.phone')}</label>
            <input
              type="tel"
              name="telefone"
              value={formData.telefone}
              onChange={handlePhoneChange}
              placeholder={t('register.phonePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('register.instagram')}</label>
            <div className="instagram-wrapper">
              <span className="instagram-prefix">@</span>
              <input
                type="text"
                name="instagram"
                value={formData.instagram}
                onChange={handleChange}
                placeholder={t('register.instagramPlaceholder')}
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('register.password')} *</label>
            <div className="senha-wrapper">
              <input
                type={showSenha ? 'text' : 'password'}
                name="senha"
                value={formData.senha}
                onChange={handleChange}
                placeholder={t('register.passwordPlaceholder')}
                required
                minLength={4}
              />
              <button
                type="button"
                className="toggle-senha-btn"
                onClick={() => setShowSenha(!showSenha)}
              >
                {showSenha ? t('register.hidePassword') : t('register.showPassword')}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>{t('register.confirmPassword')} *</label>
            <input
              type={showSenha ? 'text' : 'password'}
              name="confirmarSenha"
              value={formData.confirmarSenha}
              onChange={handleChange}
              placeholder={t('register.confirmPasswordPlaceholder')}
              required
              minLength={4}
            />
          </div>

          <div className="form-section-divider">
            <h3>{t('register.studioSection')}</h3>
          </div>

          <div className="form-group">
            <label>{t('register.studioName')}</label>
            <input
              type="text"
              name="studioNome"
              value={formData.studioNome}
              onChange={handleChange}
              placeholder={t('register.studioNamePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('register.studioCep')}</label>
            <div className="cep-input-row">
              <input
                type="text"
                name="studioCep"
                value={formData.studioCep}
                onChange={handleCepChange}
                placeholder={t('register.studioCepPlaceholder')}
                maxLength={9}
                required
              />
              <button
                type="button"
                className="cep-search-btn"
                onClick={buscarCep}
                disabled={cepLoading || formData.studioCep.replace(/\D/g, '').length !== 8}
              >
                {cepLoading ? '...' : t('common.search')}
              </button>
            </div>
            {cepError && <span className="cep-error">{cepError}</span>}
          </div>

          <div className="form-group">
            <label>{t('register.studioEndereco')}</label>
            <input
              type="text"
              name="studioEndereco"
              value={formData.studioEndereco}
              onChange={handleChange}
              placeholder={t('register.studioEnderecoPlaceholder')}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group form-group-small">
              <label>{t('register.studioNumero')}</label>
              <input
                type="text"
                name="studioNumero"
                value={formData.studioNumero}
                onChange={handleChange}
                placeholder={t('register.studioNumeroPlaceholder')}
                required
              />
            </div>
            <div className="form-group form-group-large">
              <label>{t('register.studioComplemento')}</label>
              <input
                type="text"
                name="studioComplemento"
                value={formData.studioComplemento}
                onChange={handleChange}
                placeholder={t('register.studioComplementoPlaceholder')}
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('register.studioBairro')}</label>
            <input
              type="text"
              name="studioBairro"
              value={formData.studioBairro}
              onChange={handleChange}
              placeholder={t('register.studioBairroPlaceholder')}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group form-group-large">
              <label>{t('register.studioCidade')}</label>
              <input
                type="text"
                name="studioCidade"
                value={formData.studioCidade}
                onChange={handleChange}
                placeholder={t('register.studioCidadePlaceholder')}
                required
              />
            </div>
            <div className="form-group form-group-small">
              <label>{t('register.studioEstado')}</label>
              <input
                type="text"
                name="studioEstado"
                value={formData.studioEstado}
                onChange={handleChange}
                placeholder={t('register.studioEstadoPlaceholder')}
                maxLength={2}
                required
              />
            </div>
          </div>

          {message && (
            <div className={`form-message ${success ? 'form-message-success' : 'form-message-error'}`}>
              {message}
            </div>
          )}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? t('register.submitting') : t('register.submit')}
          </button>

          {success && (
            <button type="button" className="go-login-btn" onClick={() => navigate('/login')}>
              {t('register.goToLogin')}
            </button>
          )}
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default CadastroAplicadorPage;
